<script setup lang="ts">
import { computed } from 'vue';
import { parseISO } from 'date-fns';
import Decimal from 'decimal.js';
import { formatDecimalAsString } from '@/utils/helpers/auction-numbers';
import { PRICE_PRECISION } from '@/modules/common/constants/precision';

const props = withDefaults(
  defineProps<{
    lastClosePrice?: Decimal;
    lastPrice?: Decimal;
    lastPriceAt?: string;
  }>(),
  {
    lastClosePrice: undefined,
    lastPrice: undefined,
    lastPriceAt: undefined,
  }
);

/**
 * Display a price movements with fixated width.
 * Note that the column width of the class 'has-moved' relates to the min-width of the `priceChange` col in marketplace.ts
 */
const noPriceChange = computed(
  () =>
    !props.lastClosePrice ||
    !props.lastPrice ||
    props.lastClosePrice.sub(props.lastPrice).abs().lessThan(0.01)
);

const priceChange = computed(() => props.lastPrice!.sub(props.lastClosePrice!));

const percentChange = computed(() =>
  props.lastPrice!.sub(props.lastClosePrice!).dividedBy(props.lastClosePrice!).mul(100.0)
);

// 1. Create a reactive/memoized Date object from the prop
//    This parsing only happens once (or when the prop actually changes).
const lastPriceDate = computed(() => {
  if (props.lastPriceAt) {
    return parseISO(props.lastPriceAt);
  }
  return null;
});

// blink if price was changed recently (< 5 seconds)
const isRecentlyChanged = computed(() => {
  // Check for null (meaning the prop was empty)
  if (!lastPriceDate.value) {
    return false;
  }

  const FIVE_SECONDS_MS = 5 * 1000;
  return Date.now() - lastPriceDate.value.getTime() <= FIVE_SECONDS_MS;
});

const isPriceDown = computed(() => Math.sign(priceChange.value.toNumber()) === -1);

function formatIcon(): string {
  return isPriceDown.value ? 'mdi-menu-down' : 'mdi-menu-up';
}

function formatPriceChange(): string {
  return formatDecimalAsString(priceChange.value.abs(), PRICE_PRECISION);
}

function formatPercentChange(): string {
  return formatDecimalAsString(percentChange.value.abs(), PRICE_PRECISION);
}
</script>

<template>
  <span v-if="noPriceChange" class="not-changed">&minus;</span>
  <div
    v-else
    class="has-changed"
    :class="[
      isPriceDown.valueOf() ? 'modifier-minus' : 'modifier-plus',
      { highlight: isRecentlyChanged },
    ]"
  >
    <v-icon :class="isPriceDown.valueOf() ? 'modifier-minus' : 'modifier-plus'">{{
      formatIcon()
    }}</v-icon>
    <div>
      <span class="sign">{{ isPriceDown.valueOf() ? '–' : '+' }}</span>
      <span>{{ formatPriceChange() }}</span>
    </div>
    <div>
      <span>(</span>
      <span v-if="isPriceDown.valueOf()" class="sign">–</span>
      <span>{{ formatPercentChange() }}</span>
      <span>%)</span>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.not-changed {
  color: var(--ag-data-color);
  margin: 0 4px 0 0;
}

.has-changed {
  display: grid;
  grid-template-columns: 12px 52px 63px; /* define column widths; relates to min-width of priceMovement() in marketplace.ts */
  gap: 0 1px;

  div {
    text-align: right;
  }
}

.modifier-plus {
  color: #4caf50;
}

.modifier-minus {
  color: #ff5252;
}

.sign {
  margin: 0 2px 0 0;
}
</style>
